import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Download, Smartphone, Monitor, Tablet, X, CheckCircle2, Share } from "lucide-react";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function InstallAppButton({ compact = false }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosHelp, setIosHelp] = useState(false);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); toast.success("FlowDesk est installé 🎉"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { const { outcome } = await deferred.userChoice; if (outcome === "accepted") setInstalled(true); } catch {}
      setDeferred(null);
    } else if (isIOS) {
      setIosHelp(true);
    } else {
      toast.info("Ouvrez le menu de votre navigateur puis « Installer l'application » / « Ajouter à l'écran d'accueil ».");
    }
  };

  const iosModal = iosHelp ? createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4" onClick={() => setIosHelp(false)} data-testid="ios-install-help">
      <div className="bg-card text-foreground border border-white/10 rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-cabinet font-bold text-lg">Installer sur iPhone / iPad</h3>
          <button onClick={() => setIosHelp(false)} className="p-1.5 rounded-md text-zinc-400 hover:bg-white/10"><X size={18} /></button>
        </div>
        <ol className="text-sm text-foreground/80 space-y-2 list-decimal pl-5">
          <li>Appuyez sur <Share size={14} className="inline -mt-0.5" /> <b>Partager</b> dans la barre Safari.</li>
          <li>Choisissez <b>« Sur l'écran d'accueil »</b>.</li>
          <li>Validez avec <b>« Ajouter »</b> — l'app FlowDesk apparaît avec son logo.</li>
        </ol>
      </div>
    </div>, document.body) : null;

  if (installed) {
    if (compact) return null;
    return (
      <div data-testid="install-app-installed" className="flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 size={16} /> Application installée sur cet appareil</div>
    );
  }

  if (compact) {
    if (!deferred && !isIOS) return null; // nothing to prompt on this device/browser
    return (
      <>
        <button data-testid="install-app-btn" onClick={install} title="Installer l'application FlowDesk" className="p-2.5 rounded-full text-klein hover:bg-klein/10 transition-colors shrink-0">
          <Download size={18} />
        </button>
        {iosModal}
      </>
    );
  }

  return (
    <div data-testid="install-app-card" className="bg-card border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-klein/15 text-klein flex items-center justify-center"><Download size={20} /></div>
        <div>
          <h3 className="font-cabinet font-bold text-base">Installer FlowDesk</h3>
          <p className="text-[12px] text-zinc-500">Ajoutez l'app à votre PC, mobile ou tablette pour un accès rapide et les notifications.</p>
        </div>
      </div>
      <div className="flex items-center gap-4 text-[11px] text-zinc-500 mb-3 mt-1">
        <span className="flex items-center gap-1"><Monitor size={13} /> PC</span>
        <span className="flex items-center gap-1"><Smartphone size={13} /> Mobile</span>
        <span className="flex items-center gap-1"><Tablet size={13} /> Tablette</span>
      </div>
      <button data-testid="install-app-cta" onClick={install} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-klein text-white text-sm font-semibold hover:brightness-110">
        <Download size={16} /> Installer l'application
      </button>
      {iosModal}
    </div>
  );
}
